import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Container } from "react-bootstrap";
import { FiArrowLeft, FiArrowRight, FiFileText } from "react-icons/fi";
import { products, getProductCategories, productPlaceholder } from "../../data/products";
import productBenefits from "../../data/productBenefits.json";
import ContactModal from "../ContactModal/ContactModal";
import NotFound from "../NotFound/NotFound";
import Seo, { breadcrumbSchema, webPageSchema } from "../Seo/Seo";
import ProductCard from "./ProductCard";
import ProductGallery from "./ProductGallery";
import ProductDetailSections from "./ProductDetailSections";
import { CATALOG_PATH } from "./Products";
import { getRelatedProducts } from "../../utils/productDiscovery";
import { getProductSeo, productSchema } from "../Seo/productSeo";

function ProductContent({ product }) {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const categories = getProductCategories(product);
  const [category] = categories;
  const seoImage = product.images[0].src === productPlaceholder.src
    ? { src: "/logo.webp", alt: "Klean Leaf logo" }
    : product.images[0];
  const path = `/products/${product.slug}`;
  const categoryPath = `/products?category=${category.id}`;
  const related = getRelatedProducts(product, products);
  const { title, description } = getProductSeo(product);
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
    { name: category.name, path: categoryPath },
    { name: product.name, path },
  ];

  return (
    <>
      <Seo title={title} description={description} path={path} image={seoImage.src} imageAlt={seoImage.alt}
        schema={[
          webPageSchema({ name: title, description, path, primaryImage: seoImage.src }),
          breadcrumbSchema(breadcrumbs),
          productSchema(product),
        ]} />
      <main className="products-page product-detail-page">
        <Container>
          <nav className="product-breadcrumbs" aria-label="Breadcrumb">
            <ol>{breadcrumbs.map((item, index) => (
              <li key={item.path}>{index === breadcrumbs.length - 1 ? <span aria-current="page">{item.name}</span> : <Link to={item.path}>{item.name}</Link>}</li>
            ))}</ol>
          </nav>
          <section className="product-detail-layout" aria-labelledby="product-title">
            <header className="product-detail-intro">
              <p className="products-eyebrow">Private Label Product</p>
              <div className="product-category-links" role="group" aria-label="Product categories">
                {categories.map((item) => (
                  <Link key={item.id} className="product-category-link" to={`/products?category=${item.id}`}>{item.name}</Link>
                ))}
              </div>
              <h1 id="product-title">{product.name}</h1>
              <span className="product-sku">SKU: {product.sku}</span>
              <p className="product-project-context">{productBenefits[product.sku]}</p>
              <button type="button" className="products-btn products-btn-primary" onClick={() => setIsContactModalOpen(true)}>Request a Private Label Quote <FiArrowRight aria-hidden="true" /></button>
            </header>
            <div className="product-detail-media"><ProductGallery images={product.images} productName={product.name} /></div>
            <div className="product-detail-copy">
              <p className="product-description">{product.description}</p>
              <dl className="product-facts">
                <div><dt>{categories.length > 1 ? "Categories" : "Category"}</dt><dd>{categories.map((item) => item.name).join(", ")}</dd></div>
                {product.catalogSku && <div><dt>Catalog SKU</dt><dd>{product.catalogSku}</dd></div>}
              </dl>
              <div className="product-specifications">
                <h2>Specifications &amp; sourcing</h2>
                <p>For a product-specific quotation, share your target market, estimated order quantity and packaging requirements.</p>
              </div>
              <div className="products-actions">
                <a className="catalog-pdf-link" href={CATALOG_PATH} target="_blank" rel="noopener noreferrer"><FiFileText aria-hidden="true" /> View Catalog PDF <span className="visually-hidden">(opens in a new tab)</span></a>
              </div>
            </div>
          </section>
          <ProductDetailSections product={product} onInquiry={() => setIsContactModalOpen(true)} />
          <section className="product-related" aria-labelledby="related-products-heading">
            <div className="product-related-heading">
              <div>
                <p className="products-eyebrow">Continue Exploring</p>
                <h2 id="related-products-heading">Related Products</h2>
              </div>
              <Link className="product-back-link" to={categoryPath}>View category <FiArrowRight aria-hidden="true" /></Link>
            </div>
            {related.length ? (
              <div className="catalog-grid">{related.map((item) => <ProductCard key={item.slug} product={item} />)}</div>
            ) : <p>Explore the full collection for more laundry and home care product formats.</p>}
            <Link className="product-back-link product-back-all" to="/products"><FiArrowLeft aria-hidden="true" /> Back to all products</Link>
          </section>
        </Container>
      </main>
      <ContactModal isModalOpen={isContactModalOpen} handleCancel={() => setIsContactModalOpen(false)} productName={product.name} productSku={product.sku} idPrefix={`product-${product.slug}`} title="Request a Private Label Quote" />
    </>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const product = products.find((item) => item.slug === slug);
  return product ? <ProductContent key={product.slug} product={product} /> : <NotFound />;
}
